import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell, CheckCircle2, Clock, X, RotateCcw, Search, ExternalLink,
  AlertTriangle, Info, Calendar, ChevronRight, Loader2,
} from "lucide-react";
import { useLocation } from "wouter";

// ── Types ─────────────────────────────────────────────────────────────────────

type ReminderItem = {
  id: string;
  titre: string;
  description: string | null;
  sourceType: string;
  sourceId: string | null;
  rappelDate: string;
  statut: string;
  lienUrl: string | null;
  priorite: string;
  assignePrenom: string | null;
  assigneNom: string | null;
  traiteAt: string | null;
  isTaskReminder?: boolean;
};

// ── Config ────────────────────────────────────────────────────────────────────

const PRIORITE_CONFIG: Record<string, { label: string; color: string }> = {
  basse:   { label: "Basse",   color: "bg-slate-100 text-slate-700 border-slate-200" },
  moyenne: { label: "Moyenne", color: "bg-blue-100 text-blue-700 border-blue-200" },
  haute:   { label: "Haute",   color: "bg-orange-100 text-orange-700 border-orange-200" },
};

const SOURCE_LABELS: Record<string, string> = {
  client:  "Client",
  mission: "Mission",
  ticket:  "Ticket",
  action:  "Action",
  manuel:  "Manuel",
  prospect: "Prospect",
  revendeur: "Revendeur",
  commercial: "Commercial",
};

// ── Hook rappels ──────────────────────────────────────────────────────────────

function useReminders(tab: string, search: string) {
  return useQuery<ReminderItem[]>({
    queryKey: ["/api/reminders", tab, search],
    queryFn: async () => {
      const params = new URLSearchParams({ statut: tab, search });
      const res = await authFetch(`/api/reminders?${params}`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 60_000,
  });
}

function useReminderCount() {
  return useQuery<{ count: number }>({
    queryKey: ["/api/reminders/count"],
    queryFn: async () => {
      const res = await authFetch("/api/reminders/count");
      if (!res.ok) return { count: 0 };
      return res.json();
    },
    refetchInterval: 60_000,
  });
}

// ── Composant ReminderCard ────────────────────────────────────────────────────

function ReminderCard({
  item,
  onTraite,
  onIgnore,
  onReporter,
  isPending,
}: {
  item: ReminderItem;
  onTraite: (id: string) => void;
  onIgnore: (id: string) => void;
  onReporter: (id: string, date: string) => void;
  isPending: boolean;
}) {
  const [_, navigate] = useLocation();
  const [reportDate, setReportDate] = useState("");
  const [showReportPicker, setShowReportPicker] = useState(false);

  const prioriteConf = PRIORITE_CONFIG[item.priorite] || PRIORITE_CONFIG.moyenne;
  const rappelDate = new Date(item.rappelDate);
  const isOverdue = rappelDate < new Date();

  return (
    <div
      className={cn(
        "border rounded-lg p-3 space-y-2 transition-all",
        item.isTaskReminder
          ? "border-amber-200 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-950/20"
          : "border-border bg-card hover:bg-secondary/30"
      )}
      data-testid={`reminder-card-${item.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
            {item.isTaskReminder && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-700 bg-amber-50">
                🔔 Action relancée
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 border", prioriteConf.color)}>
              {prioriteConf.label}
            </Badge>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
              {SOURCE_LABELS[item.sourceType] || item.sourceType}
            </Badge>
          </div>
          <p className="text-sm font-medium leading-snug" data-testid={`text-reminder-titre-${item.id}`}>
            {item.titre}
          </p>
          {item.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {item.description}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
            <span className={cn("flex items-center gap-1", isOverdue && item.statut === 'actif' && "text-red-600 font-medium")}>
              <Clock className="h-3 w-3" />
              {isOverdue && item.statut === 'actif'
                ? `En retard de ${formatDistanceToNow(rappelDate, { locale: fr })}`
                : format(rappelDate, "dd/MM/yyyy à HH:mm", { locale: fr })
              }
            </span>
            {(item.assignePrenom || item.assigneNom) && (
              <span>{item.assignePrenom} {item.assigneNom}</span>
            )}
          </div>
        </div>
        {item.lienUrl && (
          <button
            onClick={() => navigate(item.lienUrl!)}
            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
            title="Voir l'entité"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {item.statut === 'actif' && (
        <div className="flex items-center gap-1.5 pt-1 flex-wrap">
          <Button
            size="sm"
            variant="default"
            className="h-7 text-xs px-2.5 bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onTraite(item.id)}
            disabled={isPending}
            data-testid={`btn-reminder-traite-${item.id}`}
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Traité
          </Button>

          <Popover open={showReportPicker} onOpenChange={setShowReportPicker}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-7 text-xs px-2.5" data-testid={`btn-reminder-reporter-${item.id}`}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reporter
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" side="top">
              <p className="text-xs font-medium mb-2">Nouvelle date de rappel</p>
              <div className="flex flex-col gap-1.5 mb-2">
                {[
                  { label: "Dans 1 semaine", days: 7 },
                  { label: "Dans 1 mois", days: 30 },
                  { label: "Dans 3 mois", days: 90 },
                ].map(({ label, days }) => (
                  <button
                    key={days}
                    className="text-xs text-left px-2 py-1.5 rounded hover:bg-secondary transition-colors"
                    onClick={() => {
                      const d = new Date(Date.now() + days * 24 * 3600 * 1000);
                      onReporter(item.id, d.toISOString());
                      setShowReportPicker(false);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Input
                  type="datetime-local"
                  className="h-7 text-xs flex-1"
                  value={reportDate}
                  onChange={e => setReportDate(e.target.value)}
                />
                <Button
                  size="sm"
                  className="h-7 text-xs px-2"
                  disabled={!reportDate}
                  onClick={() => {
                    if (reportDate) {
                      onReporter(item.id, new Date(reportDate).toISOString());
                      setShowReportPicker(false);
                      setReportDate("");
                    }
                  }}
                >
                  OK
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs px-2.5 text-muted-foreground hover:text-destructive"
            onClick={() => onIgnore(item.id)}
            disabled={isPending}
            data-testid={`btn-reminder-ignorer-${item.id}`}
          >
            <X className="h-3 w-3 mr-1" />
            Ignorer
          </Button>
        </div>
      )}

      {item.statut !== 'actif' && (
        <div className="text-xs text-muted-foreground">
          {item.statut === 'traite' && item.traiteAt && (
            <span className="flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="h-3 w-3" />
              Traité le {format(new Date(item.traiteAt), "dd/MM/yyyy", { locale: fr })}
            </span>
          )}
          {item.statut === 'ignore' && <span className="text-slate-400">Ignoré</span>}
          {item.statut === 'reporte' && (
            <span className="flex items-center gap-1 text-blue-600">
              <RotateCcw className="h-3 w-3" />
              Reporté au {format(rappelDate, "dd/MM/yyyy", { locale: fr })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Composant principal RemindersPanel ────────────────────────────────────────

interface RemindersPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RemindersPanel({ isOpen, onClose }: RemindersPanelProps) {
  const [tab, setTab] = useState<"actif" | "a_venir" | "traite">("actif");
  const [search, setSearch] = useState("");
  const [_, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data: items = [], isLoading } = useReminders(tab, search);

  const mutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      return (await authFetch(`/api/reminders/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      })).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reminders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/reminders/count"] });
    },
  });

  const handleTraite = (id: string) => mutation.mutate({ id, updates: { statut: "traite" } });
  const handleIgnore = (id: string) => mutation.mutate({ id, updates: { statut: "ignore" } });
  const handleReporter = (id: string, date: string) => mutation.mutate({ id, updates: { statut: "reporte", rappelDate: date } });

  const tabs = [
    { key: "actif",   label: "À traiter",   icon: AlertTriangle },
    { key: "a_venir", label: "À venir",      icon: Calendar },
    { key: "traite",  label: "Traités",      icon: CheckCircle2 },
  ] as const;

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:w-[480px] flex flex-col p-0" data-testid="reminders-panel">
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              Centre de rappels
            </SheetTitle>
          </div>
          <div className="flex gap-1 mt-2">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors",
                  tab === key
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary/60 text-muted-foreground hover:bg-secondary"
                )}
                data-testid={`tab-reminders-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </SheetHeader>

        <div className="px-4 py-2 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Rechercher..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="input-reminder-search"
            />
          </div>
        </div>

        <ScrollArea className="flex-1 px-4 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                {tab === "actif" ? "Aucun rappel à traiter" :
                 tab === "a_venir" ? "Aucun rappel dans les 7 prochains jours" :
                 "Aucun rappel traité récemment"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map(item => (
                <ReminderCard
                  key={item.id}
                  item={item}
                  onTraite={handleTraite}
                  onIgnore={handleIgnore}
                  onReporter={handleReporter}
                  isPending={mutation.isPending}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="px-4 py-3 border-t shrink-0">
          <Button
            variant="outline"
            className="w-full text-sm"
            onClick={() => { navigate("/rappels"); onClose(); }}
            data-testid="btn-all-reminders"
          >
            Voir tous les rappels
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { useReminderCount };
